import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';
import { compact, find, range } from 'lodash';

import { Cheque, Cheque as ChequeType } from '@baemingo/table-events/dist/types';
import { useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';

import Ordering from '../Ordering';
import ChequeDetails from '../ChequeDetails';
import Table from './Table';
import ChequePanel from './ChequePanel/index';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import CartProvider from '~Components/CartProvider';
import { useDrop } from 'react-dnd';
import { DragTypes } from '~ComponentsImproved/TSR/Types';
import Icon from '~ComponentsImproved/Icon';
import UpIcon from '~Assets/icons/expand_less.svg';
import DownIcon from '~Assets/icons/expand_more.svg';
import { Message } from '~Components/PredefinedMessages';
import TableLayout from './TableLayout';
import Onboarding from '~Scenes/Onboarding';
import AccessBlocked from '~Providers/LicensesProvider/Components/AccessBlocked';
import ClaimLicense from '~Providers/LicensesProvider/Components/ClaimLicense';
import { BAEMINGO_PRODUCT } from '../../../../../__queries__';
import ConnectionPopover from '~Scenes/Tables/Components/ConnectionPopover';
import FailedPrintsAlert from '~Scenes/Handy/Components/FailedPrintsAlert';
import { useStorage } from '~Components/Storage';
import SelectOptionModal from '~Components/SelectOptionModal';

const Wrapper = styled.div`
  height: 100%;
  width: 100vw;
  position: relative;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  overflow-x: hidden;
  border-top: 1px solid var(--colorBlueGray1100);
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  width: calc(200vw - 80px);
  height: 100%;
  background: #1c232c;
  position: relative;
  transition: all 0.25s ease-in-out;
`;

const TablesWrapper = styled.div`
  flex: 0 0 calc(100vw - 80px);
  overflow-y: scroll;
  // Hide scrollbar
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.32);
  position: relative;
  z-index: 1;
`;

import { TablesSection, TablesSectionTitle } from './styles';
export { TablesSection, TablesSectionTitle } from './styles';

const ScrollHoverArea = styled.div<{ bottom?: boolean; isOver: boolean }>`
  position: ${({ bottom }) => (bottom ? 'fixed' : 'sticky')};
  top: ${({ bottom }) => (bottom ? 'auto' : '0')};
  bottom: ${({ bottom }) => (bottom ? '0' : 'auto')};
  left: 0;
  width: calc(100vw - 80px);
  height: 40px;
  transition: background-color 0.2s ease-in-out;
  background-color: ${({ isOver }) => (isOver ? 'var(--colorBlack)' : 'var(--colorBlueGray1300)')};
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

// Bounce in animation
export const OrderingWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100vw;
  height: 100%;
`;

export { TableKey, sortTablesByCode } from './types';
export type { TableComponentProps } from './types';
import { TableKey, TableComponentProps, sortTablesByCode } from './types';
import { GetTables_Venue_floorPlans_tables as TableType } from '~Scenes/Venue/__queries__';
import { makeIsTableVisible } from '~Utils/visibleTables';

/**
 * Saves a table to session storage so that it can be retrieved later
 * @param id The ID of the table to store
 */
function saveTableToRecents(id: string) {
  const recents = JSON.parse(sessionStorage.getItem('recent-tables') || '[]');

  if (!recents.includes(id)) {
    recents.unshift(id);
    recents.pop();
  }

  sessionStorage.setItem('recent-tables', JSON.stringify(recents));
}

const TableSelector: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('handy');

  const courseTypes = useSelector((state) => state.courseTypes);
  const staff = useSelector((state) => state.staff);
  const floorPlans = useSelector((state) => state.floorPlans);
  const predefinedMessages = useSelector((state) => state.predefinedMessages);
  const demoMode = useSelector((state) => state.demoMode);
  let venueId = useSelector((state) => state.venueId);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }


  const universe = useTableUniverse();
  const api = useTableApi();

  // Deleted tables are hidden, unless they still have a cheque that staff need to reach
  const isTableVisible = useMemo(() => makeIsTableVisible(universe.cheques), [universe.cheques]);
  const allTables = useMemo(
    () => floorPlans.flatMap((floorPlan) => floorPlan.tables).filter(isTableVisible),
    [floorPlans, isTableVisible],
  );
  const isScrolling = useRef(false);
  const licenses = useLicenses();

  const [justOpenedCheque, setJustOpenedCheque] = useState<string | null>(null);
  const [isOrdering, setIsOrdering] = useState<boolean>(false);
  const [chequeIsDragging, setChequeIsDragging] = useState<boolean>(false);
  const [selectedTableView, setSelectedTableView] = useState<'list' | 'map'>('list');
  const [storage] = useStorage();

  // State for double-click cheque creation with coverage modal
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [openOrderingAfterChequeCreated, setOpenOrderingAfterChequeCreated] = useState(false);
  const pendingDoubleClickTable = useRef<TableType | null>(null);

  const { selectedTable, setSelectedTable } = useTableContext();
  const { selectedCheque, setSelectedCheque } = useChequeContext();

  const cheques = universe.cheques.filter((c) => c.tableId === selectedTable?.id);

  const userCheques = universe.cheques.filter((c) => staff && c.owner === staff.id);

  const recentTableIds: string[] = JSON.parse(sessionStorage.getItem('recent-tables') || '[]');

  // Sort the tables by code for presentation
  const allTablesSorted = useMemo(() => sortTablesByCode(allTables), [allTables]);

  const recentTables = compact(recentTableIds.map((id) => find(allTables, { id })));

  const handlers = useSwipeable({
    delta: 30,
    onSwiping: () => {
      isScrolling.current = true;
    },
  });

  // Logging
  useEffect(() => {
    if (!allTables.length) {
      logger.warn('allTables returned empty array.', {
        allTables,
      });
    }

    if (!floorPlans.length) {
      logger.warn('floorPlans returned empty array.', {
        floorPlans,
        allTables,
      });
    }
  }, [allTables, floorPlans]);

  // This effect will select a newly created cheque, as soon as it is available in the universe
  useEffect(() => {
    if (justOpenedCheque && cheques.length) {
      const cheque = cheques.find((c) => c.id === justOpenedCheque);
      if (cheque) {
        setSelectedCheque(cheque);
        setJustOpenedCheque(null);

        // If triggered by double-click, open the ordering view
        if (openOrderingAfterChequeCreated) {
          setOpenOrderingAfterChequeCreated(false);
          setIsOrdering(true);
        }
      }
    }
  }, [cheques, justOpenedCheque, openOrderingAfterChequeCreated]);

  // Effect for handling post-payment UX
  useEffect(() => {
    if (!selectedCheque?.id || isOrdering) {
      return;
    }
    // If we don't have any cheques, prob due to payment, go back.
    if (!cheques.length) {
      logger.info('No cheques on table, going back to tables view.', {
        table: selectedTable,
      });
      return handleBack();
    }
    // If there is no cheque with selected id, select another one
    if (!cheques.map((c) => c.id).includes(selectedCheque?.id)) {
      setSelectedCheque(cheques[0]);
    }
  }, [cheques]);

  // Unique list of tables that the user has cheques on
  const userTableIds = userCheques
    .map((c) => c.tableId)
    .filter((value, index, self) => self.indexOf(value) === index);
  const userTables = allTablesSorted
    .filter((t) => userTableIds.includes(t.id))
    .slice(0, 8);

  const handleCreateCheque = (coverage?: number) => {
    const id = api.createCheque(selectedTable?.id, coverage ?? 0);

    // Here we would like to select the cheque but since it was just created, we
    // will not find it in the cheques array. Defer the selection to the "justOpened" effect
    setJustOpenedCheque(id);

    logger.info('Staff created a cheque.', {
      table: selectedTable,
      staff,
    });
  };

  const handleSelectCheque = (cheque: ChequeType) => {
    if (!selectedTable || !cheque) {
      return;
    }
    setSelectedCheque(cheque);
    saveTableToRecents(selectedTable.id);
  };

  const handleGoToTable = () => {
    logger.info('Staff used Go to table.', {
      table: selectedTable,
      staff,
    });

    if (!selectedTable) {
      return;
    }
    setSelectedCheque(cheques[0]);
    saveTableToRecents(selectedTable.id);
  };
  const handleOrder = () => {
    if (!selectedTable) {
      return;
    }

    saveTableToRecents(selectedTable.id);
    setIsOrdering(true);
  };

  // Handle double-click on a table: open ordering view for first cheque, or create one
  const onTableDoubleClick = useCallback(
    (table: TableType) => {
      // Select the table
      setSelectedTable(table);
      saveTableToRecents(table.id);

      // Find existing cheques for this table
      const tableCheques = universe.cheques.filter((c) => c.tableId === table.id);

      if (tableCheques.length === 1) {
        // Only one cheque - safe to go straight to ordering
        const cheque = tableCheques[0];
        logger.info('Opening ordering view via double-click (single cheque)', { tableId: table.id, chequeId: cheque.id });
        setSelectedCheque(cheque);
        setIsOrdering(true);
      } else if (tableCheques.length > 1) {
        // Multiple cheques - let the user pick which one to order on
        logger.info('Multiple cheques on table, showing cheque panel instead of ordering', { tableId: table.id, chequeCount: tableCheques.length });
        setSelectedCheque(tableCheques[0]);
      } else {
        // No cheques exist - need to create one
        if (storage.hideCoverageModal) {
          // Coverage modal disabled, create cheque with 0 coverage
          const id = api.createCheque(table.id, 0);
          logger.info('Created cheque via double-click', { tableId: table.id, chequeId: id });
          setJustOpenedCheque(id);
          setOpenOrderingAfterChequeCreated(true);
        } else {
          // Show coverage modal, then create cheque
          pendingDoubleClickTable.current = table;
          setShowCoverageModal(true);
        }
      }
    },
    [universe.cheques, storage.hideCoverageModal, api, logger],
  );

  // Handle coverage selection from the double-click coverage modal
  const handleDoubleClickCoverage = useCallback(
    (count: number) => {
      const table = pendingDoubleClickTable.current;
      if (table) {
        const id = api.createCheque(table.id, count);
        logger.info('Created cheque via double-click with coverage', { tableId: table.id, chequeId: id, coverage: count });
        setJustOpenedCheque(id);
        setOpenOrderingAfterChequeCreated(true);
      }
      setShowCoverageModal(false);
      pendingDoubleClickTable.current = null;
    },
    [api, logger],
  );

  const handleMergeCheques = useCallback(
    (chequeId: string, targetId: string) => {
      api.mergeCheque(chequeId, targetId);

      logger.info('Merging cheques.', {
        fromId: chequeId,
        intoId: targetId,
        table: selectedTable,
        staff,
      });

      // If we have selected the cheque we merge into another, select the merged one instead
      // Otherwise we end up on an empty cheque
      if (chequeId === selectedCheque?.id) {
        const cheque = cheques.find((c) => c.id === targetId);
        setSelectedCheque(cheque || null);
      }

      setChequeIsDragging(false);
    },
    [selectedCheque, universe, selectedTable],
  );

  const handleBack = () => {
    setSelectedCheque(null);
    setSelectedTable(null);
  };

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  const onTouchEnd = useCallback((table: TableType, keyType?: TableKey) => {
    if (!isScrolling.current) {
      setSelectedTable(table);
    }

    if (keyType === TableKey.recent || keyType === TableKey.your_tables) {
      const message =
        keyType === TableKey.recent
          ? 'A table was clicked in "Recent tables" (Handy)'
          : 'A table was clicked in "My tables" (Handy)';

      logger.info(message);
    }

    // Prevent any accidental mini-touches. Chill a bit after scroll end
    setTimeout(() => {
      isScrolling.current = false;
    }, 100);
  }, []);

  const onSelectCheque = useCallback((cheque: Cheque) => {
    setSelectedCheque(cheque);
  }, []);

  const onSelectTable = useCallback((table: TableType) => {
    setSelectedTable(table);
  }, []);

  const [{ isOver: isOverBottom }, bottomHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [{ isOver: isOverTop }, topHover] = useDrop({
    accept: [DragTypes.CHEQUE],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  useEffect(() => {
    let animationFrameId: number | null = null;

    const scrollDivRef = document.getElementById('tables-wrapper');

    const animateScroll = () => {
      if (scrollDivRef) {
        if (isOverBottom) {
          scrollDivRef.scrollTop += 10;
        }

        if (isOverTop) {
          scrollDivRef.scrollTop -= 10;
        }
      }
      animationFrameId = requestAnimationFrame(animateScroll);
    };

    if (isOverBottom || isOverTop) {
      animationFrameId = requestAnimationFrame(animateScroll);
    }

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOverTop, isOverBottom]);

  const shouldShowScrollAreas = useMemo(() => {
    const scrollWrapper = document.getElementById('tables-wrapper');

    return (
      scrollWrapper &&
      scrollWrapper?.scrollHeight > scrollWrapper?.clientHeight &&
      chequeIsDragging &&
      selectedTableView === 'list' // Map view has it's own scroll areas in TableMap.tsx
    );
  }, [chequeIsDragging]);

  const messagesList = useMemo(() => {
    const list: (Message | string)[] = [];
    predefinedMessages?.forEach((item) => {
      if (item.type === 'CHEQUE') {
        list.push(item.message);
      }
    });

    const sortedCourseTypes = [...courseTypes].sort((a, b) => a.order - b.order);
    sortedCourseTypes.forEach((item) => {
      list.push({ id: item.id, label: item.label });
    });
    return list;
  }, [courseTypes]);

  const TableComponent = ({ table, keyType, isMapView }: TableComponentProps) => {
    return (
      <Table
        key={`${TableKey[keyType]}_${table.id}`}
        keyType={keyType}
        table={table}
        selected={selectedTable?.id === table.id}
        onSelectCheque={onSelectCheque}
        onSelectTable={onSelectTable}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onDoubleClick={onTableDoubleClick}
        isMapView={isMapView}>
        {table.code}
      </Table>
    );
  };

  if (licenses.isPOSAccessBlocked) {
    return <AccessBlocked isHandy />;
  }

  /** We don't have a license that gives us access to tables but there is one claimable */
  const handheldOrderClaimable = (!licenses.hasHandheldOrder || !licenses.hasHandheldPro) && licenses.isHandheldOrderClaimable;
  const handheldProClaimable = (!licenses.hasHandheldOrder || !licenses.hasHandheldPro) && licenses.isHandheldProClaimable;

  if (handheldOrderClaimable) {
    return <ClaimLicense isHandy product={BAEMINGO_PRODUCT.HANDHELD_ORDER} />;
  }

  if (handheldProClaimable) {
    return <ClaimLicense isHandy product={BAEMINGO_PRODUCT.HANDHELD_PRO} />;
  }

  if (!venueId) {
    return null;
  }

  return (
    <Wrapper>
      <ConnectionPopover />
      <Onboarding isHandy />
        <Content
          style={{
            transform: `translateX(${selectedCheque ? 'calc(80px - 100vw)' : '0vw'})`,
          }}>
          <TablesWrapper
            id="tables-wrapper"
            {...handlers}
            onTouchStart={() => {
              // If we are in the details page, ignore this event
              if (selectedCheque) {
                return;
              }

              // Unselect the current table
              setSelectedTable(null);
            }}>
            <FailedPrintsAlert />

            {shouldShowScrollAreas && (
              <ScrollHoverArea ref={topHover} isOver={isOverTop}>
                <Icon src={UpIcon} />
              </ScrollHoverArea>
            )}

            {/* We don't show recent and user tables in map view since it's not relevant */}
            {selectedTableView === 'list' && (
              <>
                {staff && (
                  <TablesSection>
                    <TablesSectionTitle>{i18n('Handy.Tables.StaffTables')}</TablesSectionTitle>
                    {userTables.map((table) => (
                      <TableComponent
                        key={table.id}
                        table={table}
                        keyType={TableKey.your_tables}
                      />
                    ))}
                  </TablesSection>
                )}
                <TablesSection>
                  <TablesSectionTitle>{i18n('Handy.Tables.Recent')}</TablesSectionTitle>
                  {recentTables.length > 0 ? (
                    recentTables.map((table) => (
                      <TableComponent key={table.id} table={table} keyType={TableKey.recent} />
                    ))
                  ) : (
                    <div
                      style={{
                        color: '#aaa',
                        margin: 10,
                      }}>
                      {i18n('Handy.Tables.NoRecent')}
                    </div>
                  )}
                </TablesSection>
              </>
            )}

            <TableLayout
              allTablesSorted={allTablesSorted}
              floorPlans={floorPlans}
              chequeIsDragging={chequeIsDragging}
              selectedTableView={selectedTableView}
              setSelectedTableView={setSelectedTableView}
              tableComponent={TableComponent}
            />
          </TablesWrapper>
          {shouldShowScrollAreas && (
            <ScrollHoverArea ref={bottomHover} bottom isOver={isOverBottom}>
              <Icon src={DownIcon} />
            </ScrollHoverArea>
          )}

          <ChequePanel
            cheques={cheques}
            onCreateCheque={handleCreateCheque}
            onSelectCheque={handleSelectCheque}
            onMergeCheque={handleMergeCheques}
            onGoToTable={handleGoToTable}
            onOrder={handleOrder}
            chequeIsDragging={chequeIsDragging}
            setChequeIsDragging={setChequeIsDragging}
            goToTables={() => setSelectedCheque(null)}
            messagesList={messagesList}
          />

          <ChequeDetails />
        </Content>

      {/* Anchor for portals (e.g. ChequeDetails' ItemModifications) that need to
          escape the page's CSS translate without going all the way to document.body */}
      <div id="tsr-portal-anchor" />

      {isOrdering && (
        <CartProvider isTSR>
          <Ordering
            onSubmit={() => {
              setIsOrdering(false);
              setSelectedCheque(null)
            }}
            onCancel={() => {
              setIsOrdering(false);
            }}
          />
        </CartProvider>
      )}

      {showCoverageModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => handleDoubleClickCoverage(count as number)}
          onCancel={() => {
            setShowCoverageModal(false);
            pendingDoubleClickTable.current = null;
          }}
        />
      )}
    </Wrapper>
  );
};

export default TableSelector;
